/**
 * Warning:
 * This form creates FUNDRAISERS!
 * For legacy reasons, they are Deeds with __t = 'Campaign' and are still referred to as Campaigns in the codebase.
 */

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { reduxForm, Field, Fields, FieldArray, SubmissionError } from 'redux-form/immutable';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import InputLabel from '@material-ui/core/InputLabel';
import { Radio, RadioGroup, FormControlLabel, Tooltip } from '@material-ui/core/';
import FormControl from '@material-ui/core/FormControl';
import type { InjectedFormProps, FormSubmitHandler, SubmitHandler } from 'redux-form';
import { type Map as ImmutableMap, List, Map } from 'immutable';

import { scrollToError } from 'utils/scroll.helper';
import FormDescriptionRteField from 'retired/FormDescriptionRteField';
import FormTextField from 'retired/FormTextField';
import FormDateWithStartAndEnd from 'retired/FormDateWithStartAndEnd';
import FormPartnerSelector from 'retired/FormPartnerSelector';
import FormNonprofitsSelector from 'retired/FormNonprofitsSelector';
import FormValueSelector from 'retired/FormValueSelector';
import FormPictureSelector from 'retired/FormPictureSelector';
import FormCheckbox from 'retired/FormCheckbox';
import FormPublishingStatus from 'retired/FormPublishingStatus';
import FormRadioButton from 'retired/FormRadioButton';
import StepperComponent, { Stepper } from 'retired/Stepper';
import { red } from 'utils/color-styles';
import FormLocationSelector from 'retired/FormLocationSelector';
import FormFormsSelector from 'retired/FormFormsSelector';
import { currencies } from 'utils/currencyFormat';
import * as Actions from 'containers/DeedFormPage/actions';
import { showErrorAction } from 'containers/Notification/actions';
import {
  SelectCommunities,
  SelectOrganizingCommunity,
  useCommunitiesList,
} from 'containers/DeedFormPage/CommunitySelect';
import { Features } from 'utils/features';
import { truthy } from 'utils/truthy';
import FormSelectedAndOptedInCompanies from 'retired/FormSelectedAndOptedInCompanies';
import type { State } from 'reducers';
import { selectUploadedFile } from 'containers/DeedFormPage/selectors';
import { selectCampaigns } from 'entities/campaign/selectors';
import { selectCategories, selectERGs, selectPillars, selectSDGs } from 'entities/cause/selectors';
import { selectForms } from 'entities/form/selectors';
import {
  selectAccessibleUserLocations,
  selectOrganization,
  selectUser,
} from 'entities/user/selectors';
import { selectPartners } from 'entities/partners/selectors';
import { selectPictures } from 'entities/picture/selectors';
import { formSyncErrors, pickFormFields, type FormData } from 'containers/DeedFormPage/formUtils';
import { useDeedFormQueryParams } from 'containers/DeedFormPage/queryParams';
import { OrganizationType } from 'generated/graphql';
import { Privileges } from 'utils/privileges';
import { useQueryParams } from 'utils/useQueryParams';

import { FORM_IDENTIFIER } from '../constants';
import { shareTooltip } from '../copy';
import {
  isEmail,
  isNumber,
  maxLength5,
  maxLength10,
  maxLength5000,
  notEmpty,
  required,
  requiredRadioButton,
} from '../validators';

const formValuesSelector = pickFormFields([
  'categories',
  'companyOrPublic',
  'sharedWithCompanies',
  'featured',
  'forms',
  'id',
  'nonprofits',
  'partner',
  'pictures',
  'public',
  'status',
]);

const DeedCampaignForm = ({ handleSubmit, change }: Props): JSX.Element | null => {
  const { deedType, deed } = useDeedFormQueryParams();
  const organization = useSelector(selectOrganization);
  const campaigns = useSelector(selectCampaigns);
  const pillars = useSelector(selectPillars);
  const ERGs = useSelector(selectERGs);
  const categories = useSelector((state: State) => selectCategories(state, true));
  const SDGs = useSelector(selectSDGs);
  const forms = useSelector(selectForms);
  const partners = useSelector(selectPartners);
  const uploadedFile = useSelector(selectUploadedFile);
  const pictures = useSelector(selectPictures);
  const locations = useSelector(selectAccessibleUserLocations);
  const user = useSelector(selectUser);

  const formValues = useSelector(formValuesSelector);
  const {
    categories: selectedCategories,
    companyOrPublic,
    featured: isFeatured,
    id,
    nonprofits,
    partner: selectedPartner,
    pictures: selectedPictures,
  } = formValues;
  const status = formValues.status || 'draft';

  const errors = useSelector(formSyncErrors);

  const dispatch = useDispatch();

  const queryParams = useQueryParams();
  const duplicate = queryParams.get('duplicate');

  const [timeZone, setTimeZone] = useState<string>(
    deed?.timeZone || Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone
  );

  const { communities } = useCommunitiesList();
  const enableValidation = status !== 'draft';
  const [currencyCode, setCurrency] = useState(deed?.currencyCode || 'USD');
  useEffect(() => {
    setCurrency(nonprofits?.get(0)?.get('currencyCode') || 'USD');
  }, [nonprofits]);

  const isCompany = organization?.isCompany();

  const isCommunityFeatureEnabled = user?.hasFeature(Features.COMMUNITIES);
  const userCanViewCommunities =
    isCommunityFeatureEnabled && user?.hasPrivilege(Privileges.COMMUNITIES_VIEW);

  const deedApprovalRequired =
    partners?.get(selectedPartner)?.deedApprovalRequired &&
    user?.organization?.type === OrganizationType.Nonprofit;

  const ensureCompanyOnlyDeeds = organization?.hasSetting('ensureCompanyOnlyDeeds');

  useEffect(() => {
    if (ensureCompanyOnlyDeeds && (id === 'new' || id === 'past')) {
      change('requireAuthentication', true);
      change('public', false);
    }
  }, [organization]);

  useEffect(() => {
    // @NOTE-RT: When an NPO organizer with a corporate partner removes that partner, make them reselect `companyOrPublic`
    if (!isCompany && !selectedPartner && companyOrPublic === 'company') {
      change('companyOrPublic', null);
    }
  }, [companyOrPublic, isCompany, selectedPartner]);

  if (!organization) {
    return null;
  }

  const invalid = Map.isMap(errors) ? errors.size !== 0 : Boolean(Object.keys(errors).length);

  const submit: FormSubmitHandler<ImmutableMap<FormData, string>> = (
    formData: ImmutableMap<FormData, string>
  ) => {
    const data = formData.toJS() as FormData;

    if (status !== 'draft' && invalid) {
      // @NOTE-EV: we can't use ReduxForm onSubmitFail prop since combining it with persistentSubmitErrors breaks its behavior
      scrollToError(errors);
      // @NOTE-EV: needs to be thrown manually because FieldArray relies on `submitFail` bool to display error message when the user hasn't interacted with the section.
      throw new SubmissionError({});
    } else if (enableValidation && isCompany && !data.nonprofits) {
      dispatch(showErrorAction('Please select a partner organization'));
    } else {
      dispatch(
        Actions.saveCampaignAction({
          ...data,
          deedType,
          currencyCode,
          timeZone,
        })
      );
    }
  };

  const isExternal = deed?.get('externalId');
  const isAdmin = user?.type === 'Admin';

  const partnerPillars = pillars.filter(
    (pillar) =>
      !!pillar &&
      (isCompany || pillar.organization === selectedPartner || (isAdmin && pillar.individualsOnly))
  );
  const partnerERGs = ERGs.filter(
    (ERG) =>
      !!ERG &&
      !isCommunityFeatureEnabled &&
      (isCompany || ERG.organization === selectedPartner || (isAdmin && ERG.individualsOnly))
  );
  const showCampaignsAndPillarsAndERGs =
    (isAdmin || isCompany || selectedPartner) &&
    (campaigns.size || partnerPillars.size || partnerERGs.size || communities.size);

  const initialSharedWithCompanies = List(
    user?.organization?.siblingSubsidiaries?.map((company) => company.id).filter(Boolean) || []
  );

  const currencySymbol = currencies[currencyCode]?.symbol;

  const publicToolTip =
    "Selecting 'Public' will enable any individual to view or donate to this fundraiser, without being required to login. Additionally, this fundraiser will be publicly available to all Deed users.";

  const companyToolTip = isCompany
    ? "Selecting 'Company Only' will require users to login to verify they are company employees before they can view or donate to this fundraiser."
    : "Selecting 'Company Only' will require users to login to verify they are company employees of the Partner Company selected above before they can view or donate to this fundraiser.";

  let statusText = 'Submit';
  if (status === 'draft') {
    statusText = 'Save draft';
  } else if (status === 'pending') {
    statusText = 'Submit for review';
  }

  type StepProps = { index: number };
  const steps: ((props: { index: number }) => JSX.Element)[] = [
    ({ index }: StepProps): JSX.Element => (
      <StepperComponent key="name" labelText="Fundraiser Name *" componentIndex={index}>
        <Field
          name="name"
          component={FormTextField}
          fullWidth
          autoFocus
          inputProps={{ maxLength: 100, autoComplete: 'off' }}
          label="What would you like to name your fundraiser? *"
          helperText="Choose an exciting name for your fundraiser! (40 characters max)"
          autoComplete="off"
          validate={required}
        />
      </StepperComponent>
    ),

    showCampaignsAndPillarsAndERGs &&
      (({ index }: StepProps): JSX.Element => (
        <StepperComponent
          key="erg-pillar-community"
          labelText={`${!isCompany ? 'Partner ' : 'Campaigns, '}causes & ${
            userCanViewCommunities ? 'Communities' : 'ERGs'
          }`}
          componentIndex={index}
        >
          <SelectOrganizingCommunity communities={communities} />
          {campaigns.size > 0 && (
            <>
              <FieldArray
                name="campaigns"
                component={FormValueSelector}
                selectMultiple
                options={campaigns}
                labelText="Include in campaign"
              />
              <br />
              <br />
            </>
          )}
          {partnerPillars.size > 0 && (
            <FieldArray
              name="pillars"
              component={FormValueSelector}
              options={partnerPillars}
              labelText="Which cause area best describes your deed? (Select a maximum of five.)"
              selectMultiple
              validate={maxLength5}
              maxAmountAllowed={5}
            />
          )}
          {partnerERGs.size > 0 && (
            <>
              <br />
              <br />
              <FieldArray
                name="ERGs"
                component={FormValueSelector}
                selectMultiple
                options={partnerERGs}
                labelText="Which ERGs match your Deed? (you may choose more than one)"
              />
            </>
          )}
          <SelectCommunities communities={communities} />
        </StepperComponent>
      )),

    ({ index }: StepProps): JSX.Element => (
      <StepperComponent
        key="categories-sdgs"
        labelText={`Fundraiser Categories${SDGs.size > 0 ? ' & SDGs' : ''} *`}
        componentIndex={index}
      >
        {errors.categories && (
          <input disabled name="categories" style={{ height: 0, width: 0, display: 'block' }} />
        )}
        <FieldArray
          name="categories"
          component={FormValueSelector}
          selectMultiple
          options={categories}
          labelText="Which categories best describe your deed? (Select a maximum of five.) *"
          validate={categories.size > 0 ? [notEmpty, maxLength5] : undefined}
        />
        {SDGs.size > 0 && (
          <>
            <br />
            <br />
            {errors.SDGs && (
              <input disabled name="SDGs" style={{ height: 0, width: 0, display: 'block' }} />
            )}
            <FieldArray
              name="SDGs"
              component={FormValueSelector}
              selectMultiple
              options={SDGs}
              labelText="Which UN SDGS best fit your deed? (Select a maximum of five.) *"
              validate={[notEmpty, maxLength5]}
            />
          </>
        )}
      </StepperComponent>
    ),

    ({ index }: StepProps): JSX.Element => (
      <StepperComponent key="pictures" labelText="Deed Picture *" componentIndex={index}>
        <FieldArray
          name="pictures"
          component={FormPictureSelector}
          options={pictures}
          pictures={selectedPictures}
          fileUploadedAction={(file: any) => dispatch(Actions.fileUploadedAction(file))}
          uploadedFile={uploadedFile}
          selectedCategories={selectedCategories}
          labelText="Choose a photo for your Deed (you may choose one of the preselected ones below or upload your own)"
          validate={notEmpty}
        />
      </StepperComponent>
    ),

    ({ index }: StepProps): JSX.Element => (
      <StepperComponent key="start-end" labelText="Start and End Date *" componentIndex={index}>
        {(errors.startingAt || errors.endingAt) && (
          <input
            disabled
            name={errors.startingAt ? 'startingAt' : 'endingAt'}
            style={{ height: 0, width: 0, display: 'block' }}
          />
        )}
        <Fields
          names={['startingAt', 'endingAt']}
          component={FormDateWithStartAndEnd}
          future
          utc={false}
          selectedTimeZone={timeZone}
          setTimeZone={setTimeZone}
          validate={{
            startingAt: required,
            endingAt: required,
          }}
        />
      </StepperComponent>
    ),

    !isExternal &&
      (({ index }: StepProps): JSX.Element => (
        <StepperComponent
          key="partner"
          labelText={isCompany ? 'Nonprofit information' : 'Partner company information'}
          componentIndex={index}
        >
          {!isCompany && (
            <Fields
              names={[
                'partner',
                'public',
                'partnerName',
                'partnerEmail',
                'partnerLocation',
                'partnerEin',
                'partnerPhone',
              ]}
              component={FormPartnerSelector}
              fetchPartnersAction={(search: any, countryCode: any, acceptsDonations: boolean) =>
                dispatch(Actions.fetchPartnersAction(search, countryCode, acceptsDonations))
              }
              partners={partners}
              isCompany={false}
              isFeatured={isFeatured}
              user={user}
              acceptsDonations
              sx={{ textTransform: 'uppercase' }}
            />
          )}
          {isCompany && (
            <>
              {errors.nonprofits && (
                <input
                  disabled
                  name="nonprofits"
                  style={{ height: 0, width: 0, display: 'block' }}
                />
              )}
              <FieldArray
                name="nonprofits"
                component={FormNonprofitsSelector}
                fetchNonprofitsAction={(search: any, countryCode: any, acceptsDonations: boolean) =>
                  dispatch(Actions.fetchPartnersAction(search, countryCode, acceptsDonations))
                }
                options={partners}
                validate={[notEmpty, maxLength10]}
                limit={10}
                forFundraiser
                acceptsDonations
              />
            </>
          )}
        </StepperComponent>
      )),

    ({ index }: StepProps): JSX.Element => (
      <StepperComponent key="details" labelText="Fundraiser detail *" componentIndex={index}>
        <Field
          name="goalAmount"
          component={FormTextField}
          fullWidth
          type="number"
          inputProps={{ min: '0', step: 'any' }}
          validate={[isNumber, required]}
          autoComplete="off"
          label={`Goal amount (${currencySymbol}) *`}
        />
        <br />
        <br />
        <Field
          name="description"
          component={FormDescriptionRteField}
          fullWidth
          multiline
          inputProps={{ maxLength: 5000, autoComplete: 'off' }}
          label="How would you describe your event?"
          helperText="Give a description for your event! (5000 characters max)"
          autoComplete="off"
          validate={maxLength5000}
        />
        <br />
        <FormControl style={{ width: '100%', height: 20 }}>
          <InputLabel shrink>
            If this fundraiser should be available only to users from a specific location, select
            the location here:
          </InputLabel>
        </FormControl>
        <Field
          name="allowedLocations"
          component={FormLocationSelector}
          props={{ locations, expanded: false }}
          style={{ marginRight: 10, marginBottom: 10 }}
          fullWidth
        />
      </StepperComponent>
    ),
    ({ index }: StepProps): JSX.Element => (
      <StepperComponent
        key="organizer"
        labelText="Fundraiser Point of Contact *"
        componentIndex={index}
      >
        <FormControl style={{ width: '100%', height: 17 }}>
          <InputLabel shrink>
            Please provide a point of contact for this Deed who will serve as a liaison for donors
            to the fundraiser
          </InputLabel>
        </FormControl>
        <Grid container spacing={3}>
          <Grid item md>
            <Field
              id="organizerName"
              name="organizerName"
              component={FormTextField}
              fullWidth
              label="Name *"
              validate={required}
              autoComplete="off"
            />
          </Grid>
          <Grid item md>
            <Field
              id="organizerEmail"
              name="organizerEmail"
              type="email"
              component={FormTextField}
              fullWidth
              label="Email *"
              validate={[required, isEmail]}
              autoComplete="off"
            />
          </Grid>
        </Grid>
      </StepperComponent>
    ),
    ({ index }: StepProps): JSX.Element => (
      <StepperComponent
        key="additional"
        labelText="Additional Optional Information"
        componentIndex={index}
      >
        {isCompany && (
          <>
            <Field name="featured" component={FormCheckbox} label="Featured Deed?" />
            <br />
          </>
        )}
        <Field
          name="linkOnly"
          component={FormCheckbox}
          label="Link only: only users with access to the link can view"
        />
      </StepperComponent>
    ),
    !isExternal &&
      (({ index }: StepProps): JSX.Element => (
        <StepperComponent key="forms" labelText="Fundraiser forms" componentIndex={index}>
          <FieldArray
            name="forms"
            component={FormFormsSelector}
            options={forms}
            labelText="These forms will show on the donation confirmation page (after
                  the user made a donation to the fundraiser). The title of the
                  form will be visible to the user. Select 1 or more from the
                  forms below:"
          />
          {organization?.parent && (
            <Field
              name="shareFormResponses"
              component={FormCheckbox}
              label="Allow form responses to be visible to other organizations"
            />
          )}
        </StepperComponent>
      )),
    (!ensureCompanyOnlyDeeds || Boolean(user?.organization?.parent)) &&
      (({ index }: StepProps): JSX.Element => (
        <StepperComponent key="visibility" labelText="Deed Visibility *" componentIndex={index}>
          <Field name="companyOrPublic" component={FormRadioButton} validate={requiredRadioButton}>
            <RadioGroup row>
              <Tooltip title={companyToolTip}>
                <FormControlLabel
                  control={<Radio />}
                  value="company"
                  label="Company Only"
                  checked={companyOrPublic === 'company'}
                  disabled={!isCompany && !selectedPartner}
                />
              </Tooltip>
              <Tooltip title={publicToolTip}>
                <FormControlLabel
                  control={<Radio />}
                  value="public"
                  label="Public"
                  checked={companyOrPublic === 'public'}
                  disabled={(isFeatured && !isAdmin) || ensureCompanyOnlyDeeds}
                />
              </Tooltip>
              {user?.organization?.parent && (
                <Tooltip title={shareTooltip}>
                  <FormControlLabel
                    control={<Radio />}
                    value="shared"
                    label="Share with other Organizations"
                    checked={companyOrPublic === 'shared'}
                    disabled={!isCompany}
                  />
                </Tooltip>
              )}
            </RadioGroup>
          </Field>
          {isCompany && user?.organization?.parent && companyOrPublic === 'shared' && (
            <Field
              name="sharedWithCompanies"
              component={FormSelectedAndOptedInCompanies}
              optedInCompanies={duplicate ? List() : deed?.optedInCompanies}
              sharedWithCompanies={formValues.sharedWithCompanies || initialSharedWithCompanies}
              validate={notEmpty}
            />
          )}
        </StepperComponent>
      )),
    ({ index }: StepProps): JSX.Element => (
      <StepperComponent key="review" labelText="Review" componentIndex={index} finalStep>
        <div>
          <p>
            {enableValidation && invalid ? (
              <span style={{ color: red }}>
                Uh oh – there is missing information that is important for volunteers to know!
                Before you are able to publish this deed, you&apos; need to finish completing the
                form. For now, your deed can be saved as a draft.
              </span>
            ) : (
              <>
                Take time to review the information you entered. The deed can be set to pending or
                published only when all mandatory sections have been filled correctly.
              </>
            )}
          </p>
          <br />
          <br />
          <Field
            name="status"
            component={FormPublishingStatus}
            label="Publishing status:"
            deedApprovalRequired={deedApprovalRequired}
          />
          <br />
          <br />
          <Button type="submit" color="primary" variant="contained">
            {statusText}
          </Button>
        </div>
      </StepperComponent>
    ),
  ].filter(truthy);

  return (
    <form onSubmit={handleSubmit(submit)}>
      <Stepper orientation="vertical">
        {steps.filter(Boolean).map((render, index) => render({ index }))}
      </Stepper>
    </form>
  );
};

type Props = InjectedFormProps<FormData> & {
  handleSubmit: SubmitHandler<ImmutableMap<FormData, string>>;
};

export default reduxForm<FormData>({
  form: FORM_IDENTIFIER,
  enableReinitialize: true,
  keepDirtyOnReinitialize: true,
  persistentSubmitErrors: true,
})(DeedCampaignForm);
